import React, { useEffect, useState } from "react";
import { ContentModal } from "../../components/content-modal/ContentModal";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { ErrorView } from "../../components/error-view/ErrorView";
import { BaseLogo, ImageLogo } from "../../../core/models/entities/BaseLogo";
import { usePageQuery } from "../../shared/hooks/usePageQuery";
import {
  PagedComponentState,
  usePagedComponentStateMachine,
} from "../../shared/hooks/usePageComponentStateMachine";
import { useRepositoriesContext } from "../../../contexts/RepositoriesContext";
import { LogoListWrapper } from "./subcomponents/logo-list.wrapper";
import { useProfileContext } from "../../../contexts/ProfileContext";
import { LogoDetailsWrapper } from "./subcomponents/logo-details.wrapper";
import { useCustomerAndVendors } from "../../shared/hooks/useCustomerAndVendors";
import { RejectionNoteDialog } from "../../components/Dialogs/rejection-note-popup.component";
import { useDialog } from "../../components/Dialogs/dialog-base";
import { LogoTitleHeader } from "../../components/Logo/LogoTitleHeader";
import { useOpenLogo } from "./vmHooks/useOpenLogo";
import { useLogoActionHandler } from "./hooks/useLogoActionsHandler";
import { consumeLogoListStaleFlag } from "../../../utils/functions/logo-refresh.util";
import { useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

export function LogoListScreen(): React.ReactElement {
  const [query, queryChanger] = usePageQuery();
  const { state, dataTransition } = usePagedComponentStateMachine();
  const { logoRepository, organizationRepository } = useRepositoriesContext();
  const [lastPage, setLastPage] = useState<number>(1);
  const [logoSelected, setLogoSelected] = useState<BaseLogo | null>(null);
  const [logoRejection, setLogoRejection] = useState<BaseLogo | null>(null);
  const { profile, role } = useProfileContext();
  const rejectionNoteDialog = useDialog();
  const { result: openResult, clearOpen } = useOpenLogo();
  const { handleGoToEditLogo } = useLogoActionHandler();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const location = useLocation();
  const [updatingLogoId, setUpdatingLogoId] = useState(
    location.state?.updatingLogoId
  );

  useEffect(() => {
    if (location.state?.updatingLogoId) {
      setUpdatingLogoId(location.state.updatingLogoId);
    }
  }, [location]);

  function showRejectionNote(logo: BaseLogo) {
    setLogoRejection(logo);
    rejectionNoteDialog.openDialog();
  }

  function handleCancelRejectionNote() {
    rejectionNoteDialog.closeDialog();
  }

  const {
    data: page,
    isSuccess: isLogosSuccess,
    isLoading: isLogosLoading,
    isError: isLogosError,
  } = logoRepository.useAllLogos(query);

  const {
    data: options,
    isSuccess: isOptionsSuccess,
    isLoading: isOptionsLoading,
    isError: isOptionsError,
  } = logoRepository.useOptions();

  const {
    customers,
    vendors,
    isError: isOrganizationsError,
    isSuccess: isOrganizationsSuccess,
    isLoading: isOrganizationsLoading,
  } = useCustomerAndVendors(organizationRepository);

  if (page && page.pageCount !== lastPage) {
    setLastPage(page.pageCount);
  }

  // Warm the next page's cache so sequential paging (1->2->3...) is instant.
  // Prefetch only -- it does not alter the current query, so price freshness
  // and the page state-machine are unchanged.
  useEffect(() => {
    if (page && page.pageIndex < page.pageCount) {
      logoRepository.prefetchLogos({
        ...query,
        page: { ...query.page, index: query.page.index + 1 },
      });
    }
  }, [page?.pageIndex, page?.pageCount, query.page.index]);

  useEffect(() => {
    if (openResult) {
      setLogoSelected(openResult);
    }
  }, [openResult]);

  useEffect(() => {
    if (consumeLogoListStaleFlag()) {
      setIsRefreshing(true);
      queryClient.invalidateQueries({ queryKey: ["logos"] }).then(() => {
        setIsRefreshing(false);
      });
    }
  }, [queryClient]);

  const isLoading =
    isOptionsLoading ||
    isLogosLoading ||
    isOrganizationsLoading ||
    isRefreshing;
  const isSuccess =
    isLogosSuccess && isOptionsSuccess && isOrganizationsSuccess;
  const isError = isOptionsError || isLogosError || isOrganizationsError;
  dataTransition(isLoading, isSuccess, isError);

  return state === PagedComponentState.Succeed ||
    state === PagedComponentState.PageLoading ? (
    <>
      <LogoListWrapper
        query={query}
        queryChanger={queryChanger}
        isPageLoading={state === PagedComponentState.PageLoading}
        lastPage={lastPage}
        onSelectLogo={setLogoSelected}
        logos={page?.results}
        profile={profile!}
        customers={customers!}
        vendors={vendors}
        options={options!}
        onViewRejectNote={showRejectionNote}
        updatingLogoId={updatingLogoId}
      />
      {logoRejection && (
        <RejectionNoteDialog
          title={<LogoTitleHeader logo={logoRejection!} />}
          dialogOptions={rejectionNoteDialog}
          approval={(logoRejection as ImageLogo).approval!}
          exclude={role!}
          onEdit={() => handleGoToEditLogo(logoRejection)}
          onCancel={handleCancelRejectionNote}
          logo={logoRejection}
        />
      )}
      <ContentModal
        open={!!logoSelected}
        onClose={() => {
          setLogoSelected(null);
          clearOpen();
        }}
      >
        <LogoDetailsWrapper
          logo={logoSelected!}
          onViewRejectNote={showRejectionNote}
          closeDialog={() => setLogoSelected(null)}
        />
      </ContentModal>
    </>
  ) : state === PagedComponentState.Failed ? (
    <ErrorView />
  ) : (
    <LoadingView />
  );
}
